import React, { useState, useEffect, useCallback } from 'react';
import {
  Sparkles, Loader2, AlertTriangle, ShieldAlert, CheckCircle, XCircle,
  Database, History, ChevronDown, ChevronRight, RefreshCw, Wand2, Shield,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const RISK_BADGE = {
  low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  high: 'bg-red-100 text-red-700 border-red-200',
};

function ago(ts) {
  if (!ts) return '';
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const JsonBlock = ({ value, label, testid }) => {
  if (value === null || value === undefined) return null;
  const isEmpty = typeof value === 'object' && Object.keys(value).length === 0;
  return (
    <div className="space-y-1">
      {label && <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{label}</p>}
      <pre className="bg-slate-950 text-slate-100 text-xs rounded-lg p-3 overflow-x-auto max-h-64 font-mono"
        data-testid={testid}>
        {isEmpty ? '{}' : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
};

// ───────────────────── Compose + Preview ─────────────────────
function ComposeTab({ token, onExecuted }) {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [instruction, setInstruction] = useState('');
  const [reason, setReason] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [plan, setPlan] = useState(null);
  const [clarification, setClarification] = useState(null);
  const [phraseInput, setPhraseInput] = useState('');
  const [execResult, setExecResult] = useState(null);

  const reset = () => {
    setPlan(null);
    setClarification(null);
    setPhraseInput('');
    setExecResult(null);
  };

  const analyze = async () => {
    if (instruction.trim().length < 3) { toast.error('Describe the change you want'); return; }
    if (reason.trim().length < 3) { toast.error('A reason is required for the audit trail'); return; }
    setAnalyzing(true);
    reset();
    try {
      const r = await fetch(`${API}/api/db-override/plan`, {
        method: 'POST', headers,
        body: JSON.stringify({ instruction: instruction.trim(), reason: reason.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail?.error || d.detail || `HTTP ${r.status}`);
      if (d.needs_clarification) {
        setClarification(d.clarification_question);
      } else {
        setPlan(d);
      }
    } catch (e) {
      toast.error(typeof e.message === 'string' ? e.message : 'Analyze failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const execute = async () => {
    if (!plan) return;
    if (plan.requires_phrase && phraseInput.trim() !== plan.confirmation_phrase) {
      toast.error('Confirmation phrase does not match');
      return;
    }
    setExecuting(true);
    try {
      const r = await fetch(`${API}/api/db-override/execute`, {
        method: 'POST', headers,
        body: JSON.stringify({ plan_id: plan.plan_id, confirmation_phrase: phraseInput.trim() || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail?.error || d.detail || `HTTP ${r.status}`);
      setExecResult(d);
      setPlan(null);
      setPhraseInput('');
      toast.success('Database override applied');
      onExecuted?.();
    } catch (e) {
      toast.error(typeof e.message === 'string' ? e.message : 'Execution failed');
    } finally {
      setExecuting(false);
    }
  };

  const phraseOk = !plan?.requires_phrase || phraseInput.trim() === plan?.confirmation_phrase;

  return (
    <div className="space-y-5" data-testid="db-override-compose">
      {/* Warning banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">
          This tool writes directly to the production database. The AI proposes a change,
          you review the exact query + affected records, then confirm. Every action is fully audited.
        </p>
      </div>

      {/* Inputs */}
      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold text-gray-600 mb-1 block">What do you want to change?</label>
          <textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            rows={3}
            placeholder='e.g. "Set the odometer reading to 45000 for inspection a1b2c3d4" or "Mark lead with mobile 9880676991 as DEAD"'
            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
            data-testid="db-override-instruction"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-600 mb-1 block">Reason for this change <span className="text-red-500">*</span></label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Customer reported wrong KM at delivery; correcting per ticket #1234"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
            data-testid="db-override-reason"
          />
        </div>
        <button
          onClick={analyze}
          disabled={analyzing}
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-60"
          data-testid="db-override-analyze-btn"
        >
          {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          Analyze with AI
        </button>
      </div>

      {/* Clarification */}
      {clarification && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="db-override-clarification">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-800">The AI needs more detail</p>
              <p className="text-sm text-blue-700 mt-1">{clarification}</p>
            </div>
          </div>
        </div>
      )}

      {/* Plan preview */}
      {plan && (
        <div className="border rounded-xl overflow-hidden" data-testid="db-override-plan">
          <div className="bg-slate-50 border-b px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-indigo-600" />
              <span className="text-sm font-semibold text-gray-900">AI Proposed Change</span>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${RISK_BADGE[plan.risk] || RISK_BADGE.medium}`}>
              {plan.risk} risk
            </span>
          </div>

          <div className="p-4 space-y-4">
            <p className="text-sm text-gray-800" data-testid="db-override-summary">{plan.human_summary}</p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <div className="bg-gray-50 rounded-lg p-2.5">
                <p className="text-gray-400 uppercase tracking-wide text-[10px]">Collection</p>
                <p className="font-mono font-semibold text-gray-900">{plan.collection}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5">
                <p className="text-gray-400 uppercase tracking-wide text-[10px]">Operation</p>
                <p className="font-mono font-semibold text-gray-900">{plan.operation}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5">
                <p className="text-gray-400 uppercase tracking-wide text-[10px]">Matched records</p>
                <p className="font-mono font-semibold text-gray-900" data-testid="db-override-matched">{plan.matched_count}</p>
              </div>
            </div>

            {plan.is_bulk && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                <p className="text-xs text-amber-800">
                  This is a <strong>bulk change</strong> affecting <strong>{plan.matched_count} records</strong>. Review carefully before confirming.
                </p>
              </div>
            )}

            {plan.resolutions?.length > 0 && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-1" data-testid="db-override-resolutions">
                <p className="text-xs font-semibold text-indigo-800">Resolved references</p>
                {plan.resolutions.map((r, i) => (
                  <p key={i} className="text-xs text-indigo-700">
                    "{r.input}" → <strong>{r.resolved_name}</strong> ({r.resolved_email}) · id <span className="font-mono">{String(r.resolved_user_id).slice(0, 12)}…</span>
                  </p>
                ))}
              </div>
            )}

            {plan.report_warning && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2" data-testid="db-override-report-warning">
                <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5" />
                <p className="text-xs text-orange-800">{plan.report_warning}</p>
              </div>
            )}

            <JsonBlock value={plan.filter} label="Filter" testid="db-override-filter" />
            {plan.operation?.startsWith('update') && <JsonBlock value={plan.update} label="Update" testid="db-override-update" />}
            {plan.operation?.startsWith('insert') && <JsonBlock value={plan.document} label="Document" testid="db-override-document" />}

            {plan.sample_docs?.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Affected records preview ({plan.sample_docs.length} shown)
                </p>
                <JsonBlock value={plan.sample_docs} testid="db-override-sample" />
              </div>
            )}

            {/* Confirmation */}
            {plan.matched_count === 0 && plan.operation !== 'insert_one' && plan.operation !== 'insert_many' ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                No records match this filter — nothing would change. Refine your instruction.
              </div>
            ) : (
              <div className="border-t pt-4 space-y-3">
                {plan.requires_phrase && (
                  <div>
                    <label className="text-xs font-semibold text-gray-700 mb-1 block">
                      To proceed, type this exact phrase:
                    </label>
                    <code className="block bg-red-50 border border-red-200 text-red-700 rounded px-3 py-1.5 text-sm font-mono mb-2 select-all">
                      {plan.confirmation_phrase}
                    </code>
                    <input
                      value={phraseInput}
                      onChange={(e) => setPhraseInput(e.target.value)}
                      placeholder="Type the confirmation phrase"
                      className="w-full border rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none"
                      data-testid="db-override-phrase-input"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={execute}
                    disabled={executing || !phraseOk}
                    className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
                    data-testid="db-override-execute-btn"
                  >
                    {executing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                    Confirm & Execute
                  </button>
                  <button
                    onClick={reset}
                    className="inline-flex items-center gap-2 border text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg"
                    data-testid="db-override-cancel-btn"
                  >
                    <XCircle className="h-4 w-4" /> Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Execution result */}
      {execResult && (
        <div className="border border-emerald-200 rounded-xl overflow-hidden" data-testid="db-override-result">
          <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-3 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-800">Change applied</span>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-sm text-gray-700">
              <span className="font-mono">{execResult.operation}</span> on{' '}
              <span className="font-mono font-semibold">{execResult.collection}</span> — {' '}
              {JSON.stringify(execResult.result)}
            </p>
            {execResult.changes?.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Field changes ({execResult.changes_count})
                </p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-500">
                      <tr>
                        <th className="px-3 py-1.5 text-left">Record</th>
                        <th className="px-3 py-1.5 text-left">Field</th>
                        <th className="px-3 py-1.5 text-left">Old</th>
                        <th className="px-3 py-1.5 text-left">New</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {execResult.changes.slice(0, 50).map((c, i) => (
                        <tr key={i}>
                          <td className="px-3 py-1.5 font-mono text-gray-600">{String(c.doc_id).slice(0, 12)}</td>
                          <td className="px-3 py-1.5 font-mono">{c.field}</td>
                          <td className="px-3 py-1.5 text-red-600 font-mono">{JSON.stringify(c.old)}</td>
                          <td className="px-3 py-1.5 text-emerald-700 font-mono">{JSON.stringify(c.new)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────── Audit / History ─────────────────────
function HistoryTab({ token }) {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('execute');

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter ? `&action=${filter}` : '';
      const r = await fetch(`${API}/api/db-override/history?limit=100${q}`, { headers });
      const d = await r.json();
      setItems(d.history || []);
    } catch (e) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  return (
    <div className="space-y-3" data-testid="db-override-history">
      <div className="flex items-center justify-between">
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
          {[{ id: 'execute', label: 'Executions' }, { id: 'plan', label: 'All Requests' }, { id: '', label: 'Everything' }].map(t => (
            <button key={t.id} onClick={() => setFilter(t.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium ${filter === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
              data-testid={`db-override-history-filter-${t.id || 'all'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <button onClick={fetchHistory} className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : items.length === 0 ? (
        <div className="text-center text-sm text-gray-400 py-16">No audit entries yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const isOpen = expanded === it.id;
            return (
              <div key={it.id} className="border rounded-lg overflow-hidden" data-testid="db-override-history-row">
                <button onClick={() => setExpanded(isOpen ? null : it.id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left">
                  <div className="flex items-center gap-3 min-w-0">
                    {isOpen ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${it.action === 'execute' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                      {it.action}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${it.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {it.ok ? 'ok' : 'error'}
                    </span>
                    <span className="text-sm text-gray-800 truncate">{it.human_summary || it.instruction}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 text-xs text-gray-400">
                    <span className="font-mono">{it.collection}</span>
                    <span>{ago(it.timestamp)}</span>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t bg-gray-50/50 px-4 py-3 space-y-3 text-xs">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div><p className="text-gray-400">User</p><p className="font-medium text-gray-800">{it.user_name || it.user_email}</p></div>
                      <div><p className="text-gray-400">When</p><p className="font-medium text-gray-800">{new Date(it.timestamp).toLocaleString()}</p></div>
                      <div><p className="text-gray-400">Operation</p><p className="font-mono text-gray-800">{it.operation}</p></div>
                      <div><p className="text-gray-400">Matched</p><p className="font-mono text-gray-800">{it.matched_count}</p></div>
                    </div>
                    <div><p className="text-gray-400">Instruction</p><p className="text-gray-800">{it.instruction}</p></div>
                    <div><p className="text-gray-400">Reason</p><p className="text-gray-800">{it.reason}</p></div>
                    {it.error && <div className="text-red-600"><p className="text-gray-400">Error</p>{it.error}</div>}
                    <JsonBlock value={it.filter} label="Filter" />
                    {it.update && Object.keys(it.update).length > 0 && <JsonBlock value={it.update} label="Update" />}
                    {it.changes?.length > 0 && (
                      <div>
                        <p className="text-gray-400 mb-1">Field changes ({it.changes.length})</p>
                        <div className="border rounded-lg overflow-hidden bg-white">
                          <table className="w-full">
                            <thead className="bg-gray-50 text-gray-500">
                              <tr><th className="px-2 py-1 text-left">Record</th><th className="px-2 py-1 text-left">Field</th><th className="px-2 py-1 text-left">Old</th><th className="px-2 py-1 text-left">New</th></tr>
                            </thead>
                            <tbody className="divide-y">
                              {it.changes.slice(0, 50).map((c, i) => (
                                <tr key={i}>
                                  <td className="px-2 py-1 font-mono text-gray-600">{String(c.doc_id).slice(0, 12)}</td>
                                  <td className="px-2 py-1 font-mono">{c.field}</td>
                                  <td className="px-2 py-1 text-red-600 font-mono">{JSON.stringify(c.old)}</td>
                                  <td className="px-2 py-1 text-emerald-700 font-mono">{JSON.stringify(c.new)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ───────────────────── Root ─────────────────────
export default function DbOverrideTab() {
  const { token, user } = useAuth();
  const [tab, setTab] = useState('compose');
  const [historyKey, setHistoryKey] = useState(0);

  const role = user?.role_code || '';
  if (!['CEO', 'ADMIN'].includes(role)) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center" data-testid="db-override-forbidden">
        <Shield className="h-8 w-8 mx-auto text-amber-500 mb-2" />
        <p className="text-sm font-semibold text-amber-800">CEO / Admin access required</p>
        <p className="text-xs text-amber-700 mt-1">DB Override writes directly to the production database. Restricted by RBAC.</p>
      </div>
    );
  }

  const tabs = [
    { id: 'compose', label: 'New Override', icon: Sparkles },
    { id: 'history', label: 'Audit Trail', icon: History },
  ];

  return (
    <div className="space-y-4" data-testid="db-override-tab">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">DB Override</h2>
        <p className="text-sm text-gray-500">Describe a data change in plain English. AI proposes the MongoDB query; you confirm before it runs.</p>
      </div>

      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`db-override-tab-${t.id}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${
              tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
            }`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'compose' && <ComposeTab token={token} onExecuted={() => setHistoryKey(k => k + 1)} />}
      {tab === 'history' && <HistoryTab token={token} key={historyKey} />}
    </div>
  );
}
